import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TicketDetailsComponent } from '../../ticket-details.component';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { TicketLikeService } from 'src/app/core/services/ticket/ticket-like.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { NoteService } from '../../../core/services/note.service';
import { ReplySortingService } from 'src/app/core/services/ticket/reply-sorting.service';
import { ThreadFilterService } from '../../../core/services/thread-filter.service';
import { finalize, take } from 'rxjs/operators';
import { TicketService } from '../../../core/services/ticket/ticket.service';
import { SocialAccountsService } from '../../../core/services/social-accounts.service';
import { FacebookPageDTO } from '@sb-tools/typescript/models/dto/facebook-page.dto';
import { Observable } from 'rxjs';
import { ReactionTooltipTextType } from '../../../core/enums/reaction-tooltip-text-type.enum';
import { OpenTicketInNewWindowService } from '../../../core/services/ticket/open-ticket-in-new-window.service';
import { ReactionsModalComponent } from '../../../shared/modal-windows/reactions-modal/reactions-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SocialMediaAccountDTO } from '@sb-tools/typescript/models/dto/social-media-account.dto';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { LikeStatus } from '../../../core/models/like-status.model';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TicketMainMessageComponent } from '../ticket-main-message/ticket-main-message.component';
import { ReactionsComponent } from './reactions/reactions.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { ThreadSortControlComponent } from '../thread-sort-control/thread-sort-control.component';
import { ThreadExpanderComponent } from '../thread-expander/thread-expander.component';
import { ReplySocialMediaComponent } from './reply-social-media/reply-social-media.component';
import { MessageThreadElementType } from '@sb-tools/typescript/enums/message-thread-element-type.enum';
import { SuggestionsComponent } from '../suggestions/suggestions.component';
import { NewReplySocialMediaComponent } from './new-reply-social-media/new-reply-social-media.component';
import { FilterRepliesPipe } from '../../../shared/pipes/filter-replies.pipe';
import { SessionAuthenticationEventComponent } from '../session-authentication-event/session-authentication-event.component';

@Component({
    selector: 'app-ticket-thread-social-media',
    templateUrl: './ticket-thread-social-media.component.html',
    styleUrls: ['./ticket-thread-social-media.component.scss'],
    imports: [
        NgClass,
        TranslateModule,
        TicketMainMessageComponent,
        ReactionsComponent,
        TooltipModule,
        InlineSVGComponent,
        BsDropdownModule,
        UserAvatarComponent,
        ThreadSortControlComponent,
        ThreadExpanderComponent,
        ReplySocialMediaComponent,
        SessionAuthenticationEventComponent,
        SuggestionsComponent,
        NewReplySocialMediaComponent,
        FilterRepliesPipe,
    ],
})
export class TicketThreadSocialMediaComponent implements OnInit, OnDestroy {
    isAnsweringFromAnotherPage: boolean;
    isApproving = false;
    isUpdatingLikeStatus: boolean;
    map;
    marker;
    reactionTooltipTextType = ReactionTooltipTextType;
    socialMediaAccounts: Array<FacebookPageDTO | SocialMediaAccountDTO>;
    socialMediaSource: FacebookPageDTO | SocialMediaAccountDTO;
    threadFilterService = ThreadFilterService;
    ticket: DetailedTicketDTO;
    ticketSourceEnum = TicketSource;
    updateReactionsMarker;

    private unsubscriber = new Subject<void>();

    constructor(
        @Inject(TicketDetailsComponent) public parent: TicketDetailsComponent,
        public commonDataService: CommonDataService,
        public noteService: NoteService,
        public openTicketInNewWindowService: OpenTicketInNewWindowService,
        public replySortingService: ReplySortingService,
        private bsModalService: BsModalService,
        private socialAccountsService: SocialAccountsService,
        private ticketLikeService: TicketLikeService,
        private ticketService: TicketService,
    ) {
        this.ticket = parent.ticket;
    }

    ngOnInit() {
        this.processTicket();
    }

    openReactionsWindow({ isTicketReactions, replyId = null }): void {
        const initialState = {
            isTicketReactions,
            replyId,
            ticketId: this.ticket.id,
            socialMediaSourceId: this.parent.socialMediaSourceId,
        };

        this.bsModalService.show(ReactionsModalComponent, {
            initialState,
            class: 'reactions-modal',
        });
    }

    toggleLike() {
        if (this.isUpdatingLikeStatus) {
            return;
        }

        this.isUpdatingLikeStatus = true;

        this.ticketLikeService
            .changeLikeStatus(
                this.ticket.id,
                this.parent.socialMediaSourceId,
                this.ticket.constraints.areReactionsAllowed ? !this.ticket.hasReactionFromTeam : !this.ticket.isLiked,
            )
            .pipe(
                finalize(() => {
                    this.isUpdatingLikeStatus = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((ticketLikeStatus: LikeStatus) => {
                if (this.ticket.constraints.areReactionsAllowed) {
                    this.ticket.hasReactionFromTeam = !this.ticket.hasReactionFromTeam;
                }

                this.ticket.reactions = ticketLikeStatus.reactions;
                this.ticket.isLiked = ticketLikeStatus.isLiked;
                this.ticket.likeCount = ticketLikeStatus.likeCount;
                this.updateReactionsMarker = new Date().getTime();
            });
    }

    processTicket() {
        if (this.ticket.location) {
            const latitude = this.ticket.location.latitude.replace(',', '.');
            const longitude = this.ticket.location.longitude.replace(',', '.');
            this.map = { center: { latitude, longitude }, zoom: 8 };

            this.marker = {
                id: 0,
                coords: {
                    latitude,
                    longitude,
                },
            };
        }

        this.parent.socialMediaSourceId = this.ticket.defaultSocialMediaSourceId;

        this.setSocialMediaAccountsData();
    }

    review(isApproved) {
        this.isApproving = true;
        this.ticketService
            .review(this.ticket.id, isApproved)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isApproving = false;
                }),
            )
            .subscribe(() => {
                this.ticket.isReviewNeeded = false;
                if (isApproved) {
                    this.ticket.isHidden = false;
                }
            });
    }

    setSocialMediaSource(socialMediaSourceId?) {
        if (socialMediaSourceId) {
            this.parent.socialMediaSourceId = socialMediaSourceId;
        }

        this.socialMediaSource = this.socialMediaAccounts.find((item) => {
            return item.id === this.parent.socialMediaSourceId;
        });
    }

    private setSocialMediaAccountsData() {
        let accounts$: Observable<FacebookPageDTO[] | SocialMediaAccountDTO[]>;

        switch (this.ticket.source) {
            case TicketSource.facebook: {
                accounts$ = this.socialAccountsService.getFacebook();
                break;
            }

            case TicketSource.instagram: {
                accounts$ = this.socialAccountsService.getInstagram();
                break;
            }

            case TicketSource.youtube: {
                accounts$ = this.socialAccountsService.getYoutube();
                break;
            }
        }

        accounts$?.pipe(takeUntil(this.unsubscriber), take(1)).subscribe((accounts) => {
            this.socialMediaAccounts = accounts;
            this.setSocialMediaSource();

            this.isAnsweringFromAnotherPage =
                this.ticket.isEditable &&
                this.ticket.constraints.isAnsweringFromAnotherPageAllowed &&
                this.socialMediaAccounts?.length > 0;
        });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }

    protected readonly ThreadFilterService = ThreadFilterService;
    protected readonly messageThreadElementTypeEnum = MessageThreadElementType;
}
